import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation} from "@angular/core";
import {SelectItem} from "primeng/api";
import {DialogService} from "primeng/dynamicdialog";
import {ExtendedComponentSchema, FormioForm, FormioOptions, FormioRefreshValue} from "@formio/angular";
import {PrismService} from "../../prism.service";
import {FormDefinitionForCreateNewRequest, FormIODefinition} from "@features/bb-formio/dto/form-definition";
import {UpdateFormRevisionRequest} from "@features/bb-formio/dto/form-revision";
import {FormIODefinitionService} from "@features/bb-formio/services/formio-definition.service";
import {UserService} from "@main/users";
import {FormIORevisionService} from "../../services/formio-revision.service";
import {FormIODataService} from "../../services/formio-data.service";
import {IEpisode} from "@project/models/episodes/episode";
import {EpisodeCreatedFrom} from "@project/models/episodes/episode_createdFrom";
import {EpisodeService} from "@project/episode/episode.service";
import {InnerTab, TabComponent} from "@main/formio/multi-user-form/components/list/multi-user-form-list.component";
import {TabObj} from "@main/formio/multi-user-form/dto/multi-user-form.dto";
import {Router, ActivatedRoute} from "@angular/router";
import {IHttpResponseHandlerSettings} from "@bbwt/modules/data-service";
import {firstValueFrom} from "rxjs";
import {FormIOInboxItemType, FormIOInboxItemTypeMap} from "@main/formio-inbox/formio-inbox-item";
import {BbFormVersionHandlerComponent} from "../bb-form-version-handler/bb-form-version-handler.component";
import {FormChangesDelta, FormFieldDataUpdate} from "../bb-form-version-handler/bb-form-version-handler.models";
import {PatientService} from "@main/app-layout/components/patient-switch/patient.service";
import { FormPdfExportMode } from "./bb-form-builder.models";


@Component({
    selector: "bb-form-builder",
    templateUrl: "./bb-form-builder.component.html",
    styleUrls: ["../../formio.styles.scss", "./bb-form-builder.component.scss"],
    encapsulation: ViewEncapsulation.None,
    providers: [DialogService]
})
export class BbFormBuilderComponent implements OnInit, AfterViewInit {
    public refreshForm: EventEmitter<FormioRefreshValue> = new EventEmitter();
    public formStatus: string | null = null;
    public anyErrors: any;
    public status: string;
    public formName: string;
    public form?: FormioForm;
    public options?: FormioOptions;
    public isSaving: boolean = false;
    public editMode: boolean = false;
    public revInputTargetingOwnTab: string[];
    public canLeaveWithoutWarning: boolean = false;


    // Track where form definition has submitted data.
    protected formHasData: boolean = false;

    // A copy of the initial form revision json.
    private initialForm: FormioForm;

    private _categories: SelectItem[];
    private _exportReports: SelectItem[];    

    moduleId: string;
    selectedCategory: string;
    protected readonly FormPdfExportMode = FormPdfExportMode;
    selectedPdfExportMode: FormPdfExportMode = FormPdfExportMode.Auto;
    selectedLinkedDashboardId: string;
    pdfExportModes: SelectItem[] = [
        <SelectItem>{ label: "None", value: FormPdfExportMode.None },
        <SelectItem>{ label: "Automatic", value: FormPdfExportMode.Auto },
        <SelectItem>{ label: "Custom PDF", value: FormPdfExportMode.CustomPDF },        
    ];

    formObject: FormIODefinition | null;
    errorWhileLoadingForm = false;
    originalForm?: FormIODefinition;
    byRequestOnly = false;
    shareOnComplete = false;
    isEditable = false;
    editableOnlyByInstantiator = false;
    formJsonChanged = false;
    mufCapable = false;

    revisionNote = "";
    revisionId = null;

    oldEpisode: IEpisode = null;
    episode: IEpisode = null;
    episodeId: string = null;

    episodeLock = false;
    episodeLockOldState = false;
    createFrom: EpisodeCreatedFrom = EpisodeCreatedFrom.Form;
    assignedClinicianId: string;
    patientId: string = null;

    displayFormSavingDialog: boolean = false;
    displayAddEpisodeDialog: boolean = false;
    displayEditEpisodeDialog: boolean = false;
    displaySelectPatientDialog: boolean = false;
    displayFormVersionChangesDialog: boolean = false;

    @Input() formDefId: string;
    @Input() formRevisionId: string;
    @Input() isNewRevision: boolean;
    @Input() mobileFriendly: boolean = false;

    @Output() readonly onChange: EventEmitter<object>;

    @ViewChild("json", {static: true}) jsonElement?: ElementRef;
    @ViewChild(BbFormVersionHandlerComponent, {static: true}) versionHandlerComponent: BbFormVersionHandlerComponent;

    constructor(
        private formIORevisionService: FormIORevisionService,
        private formIOService: FormIODefinitionService,
        private formIODataService: FormIODataService,
        private episodeService: EpisodeService,
        private patientService: PatientService,
        private activatedRoute: ActivatedRoute,
        private userService: UserService,
        public prism: PrismService,
        private router: Router) {
        this.form = { components: [] };
        this.assignedClinicianId = this.userService.currentUser.id;
    }


    @Input() set categories(value: SelectItem[]) {
        if (value) {
            this._categories = value;
            if (!this.selectedCategory && value.length > 0) {
                this.selectedCategory = value[0].value;
            }
        }
    }

    get categories(): SelectItem[] {
        return this._categories;
    }

    @Input() set exportReports(value: SelectItem[]) {
        if (value) this._exportReports = value;
    }

    get exportReports(): SelectItem[] {
        return this._exportReports;
    }


    closeAddEpisodeDialog(episode: IEpisode): void {
        if (episode) {
            episode.assignedClinicianUserId = this.assignedClinicianId;
            this.episode = episode;
            this.episodeId = this.episode.id;
        }
    }

    closeEditEpisodeDialog(episode: IEpisode) {
        this.displayEditEpisodeDialog = true;
        this.episode = episode;
    }

    cancelEditEpisode() {
        this.displayEditEpisodeDialog = true;
    }

    async deleteEpisode() {
        if (!this.episode) {
            return;
        }

        await this.episodeService.deleteEpisode(this.episode.id);
        this.episodeLock = false;
        this.episodeId = this.episode = null;
    }

    editEpisode() {
        if (this.episode) {
            this.episodeId = this.episode.id;
            this.displayEditEpisodeDialog = true;
        }
    }

    onOkSelectedPatientDialog(patientId: string) {
        this.patientId = patientId;
        this.patientService.currentPatientId = this.patientId;
        this.displaySelectPatientDialog = false
        this.displayAddEpisodeDialog = true;
    }

    cancelAddEpisode() {
        this.displayAddEpisodeDialog = false;
    }

    onClickEpisodeTitle() {
        if (this.episode == null) {
            this.displayAddEpisodeDialog = true;
        }
    }

    async ngOnInit() {
        if (this.formDefId) {
            try {
                this.formObject = await this.formIOService.get(this.formDefId);
                this.byRequestOnly = this.formObject.byRequestOnly;
                if (this.formRevisionId) {
                    const revision = await this.formIORevisionService.get(this.formRevisionId);
                    revision.json = await this.formIORevisionService.downloadContentFromStorage(revision.json);

                    if (revision) {
                        this.form = JSON.parse(revision.json);
                        this.initialForm = JSON.parse(revision.json);
                        this.mobileFriendly = revision.mobileFriendly;
                        this.shareOnComplete = revision.shareOnComplete;
                        this.isEditable = revision.isEditable;
                        this.editableOnlyByInstantiator = revision.editableOnlyByInstantiator;
                        this.revisionNote = revision.note;
                        if (this.formObject.formCategoryId) {
                            this.selectedCategory = this.formObject.formCategoryId;
                        }

                        this.selectedPdfExportMode = revision.pdfExportMode ? revision.pdfExportMode : FormPdfExportMode.Auto;

                        if (revision.linkedDashboardId) {
                            this.selectedLinkedDashboardId = revision.linkedDashboardId;
                        }
                        // check if form revision has data instances
                        this.formIODataService.checkIfFormHasData(this.formDefId)
                            .then((hasData: boolean) => {
                                this.formHasData = hasData;
                            });
                    }
                }
                if (this.formObject) {
                    this.originalForm = {...this.formObject};
                    this.formName = this.formObject.name;
                    this.editMode = true;
                    this.setJson(this.form);
                    this.oldEpisode = this.episode = this.formObject.episode;
                    this.episodeId = this.episode?.id;
                    this.episodeLockOldState = this.episodeLock = this.formObject.isEpisodeLocked;
                }
            } catch (error) {
                this.errorWhileLoadingForm = true;
            }
        }
    }

    ngAfterViewInit(): void {
        this.prism.init();
    }

    changeEvent(event: any) {
        this.formJsonChanged = true;
        this.anyErrors = this.getErrorByMixingSingleAndMultipleValueComponentsWithSameApiKey_Tag(this.getInnerComponents(event.form));
        if (this.anyErrors) {
            this.formStatus = `There are single and multiValue components using the same key ("${this.anyErrors.key}") and tag ("${this.anyErrors.tag}")`;
        }
        this.setJson(event.form);
        // update the form property
        this.refreshForm.emit({
            property: "form",
            value: event.form
        });
        // Trigger the event for external components
        if (this.onChange) {
            this.onChange.emit(event);
        }
    }

    setJson(form: any) {
        if (this.jsonElement) {
            this.jsonElement.nativeElement.innerHTML = "";
            this.jsonElement.nativeElement.appendChild(document.createTextNode(JSON.stringify(form, null, 4)));
        }
    }

    clearJson() {
        if (this.jsonElement) {
            this.jsonElement.nativeElement.innerHTML = "";
        }
    }

    clearForm() {
        this.form = {components: []};
        this.clearJson();
        if (!this.editMode) {
            this.deleteEpisode().then();
        }
    }

    formValidForSaving() {
        return !!this.formName
            && !!this.selectedCategory
            && (this.selectedPdfExportMode !== FormPdfExportMode.CustomPDF || !!this.selectedLinkedDashboardId);
    }

    public async saveForm() {
        if (!this.formName) {
            this.status = "*** Form name is required ***";
            return;
        }

        if (this.anyErrors != null) {
            return;
        }

        if (this.isSaving) {
            return;
        }

        this.isSaving = true;

        try {
            await this.doSave();
        } finally {
            this.isSaving = false;
        }
    }

    private async doSave() {
        this.status = "";
        const isNewForm = !this.formDefId || this.formDefId?.length <= 0;
        this.revInputTargetingOwnTab = [];

        const rvInputsMissingTabProperty = this.checkMissingPropertiesOnReviewerTabs(this.form).toString();

        const selectedCategoryName = this.categories.find(x => x.value == this.selectedCategory).label;
        const isSurvey = selectedCategoryName == FormIOInboxItemTypeMap[FormIOInboxItemType.Survey];

        if (rvInputsMissingTabProperty) {
            this.status = `ERROR: The following reviewer inputs are missing the target tab: ${rvInputsMissingTabProperty}`;
            return;
        }

        if (this.revInputTargetingOwnTab.length > 0) {
            this.status = `ERROR: The following reviewer inputs are targeting the same tab they are in: ${this.revInputTargetingOwnTab.toString()}`;
            return;
        }

        if (isSurvey && this.isEditable) {
            this.status = "ERROR: Survey forms cannot be editable";
            return;
        }

        try {
            const stateTabs = this.checkMUFCapableFormJSON(this.form);
            if (stateTabs > 1) {
                this.status = "*** There can be only one State-Tab component per form ***";
                return;
            }
            if (this.checkCustomGridConsultHistDuplicateJSON(this.form) > 1) {
                this.status = "*** There can be only one Consultation History grid component per form ***";
                return;
            }
            if (this.checkCustomGridEpisodeNotesDuplicateJSON(this.form) > 1) {
                this.status = "*** There can be only one Episode Notes grid component per form ***";
                return;
            }
            if (this.checkCustomGridMedHistDuplicateJSON(this.form) > 1) {
                this.status = "*** There can be only one Medication History grid component per form ***";
                return;
            }

            this.mufCapable = stateTabs > 0;

            if (this.mufCapable && this.isEditable) {
                this.status = "ERROR: Multi-User forms cannot be editable";
                return;
            }

            if (this.byRequestOnly && this.isEditable) {
                this.status = "ERROR: By Request Forms cannot be editable";
                return;
            }

            if (isNewForm) {
                // create a new FormDefinition => FormRevision 1.0
                const newFormDefinitionCreated = await this.createNewFormDefinition();
                return await this.setAllDone(newFormDefinitionCreated);
            }

            // Detect changes applied to the form definition.
            const changesDelta: FormChangesDelta = this.versionHandlerComponent.getFormChangesDelta(this.initialForm, this.form);

            // If there are changes, open data version handler to select changes to apply and set default values to data.
            if (this.versionHandlerComponent.hasFormChanges(changesDelta)) {
                this.versionHandlerComponent.bindFormChangesTree(changesDelta);
                this.displayFormVersionChangesDialog = true;

                // Otherwise, open form saving dialog to let the user decide if this is a major or minor change.
            } else this.displayFormSavingDialog = true;

        } catch (error) {
            this.setAllDone(false).then();
            throw error;
        }
    }

    protected async saveFormChanges(breakingChanges: boolean) {
        if (this.displayFormVersionChangesDialog) {
            // Update the form with the selected changes.
            this.form = this.versionHandlerComponent.applySelectedFormChanges(this.form);
        }

        const revisionUpdated: boolean = await this.updateRevision(!breakingChanges, breakingChanges);

        // Update the form data with the new default values only if the changes in the form could be saved.
        if (this.displayFormVersionChangesDialog && revisionUpdated) {
            // Update the existing form data with the specified default values for the new fields.
            const updates: FormFieldDataUpdate[] = this.versionHandlerComponent.getFormDataUpdates();
            if (!!updates?.length) this.formIODataService.updateFormData(this.formDefId, updates).then();
        }

        this.setAllDone(revisionUpdated).then();
        this.displayFormVersionChangesDialog = false;
        this.displayFormSavingDialog = false;
    }

    private async createNewFormDefinition(formDefinitionName: string = null) {
        const formJsonDefinition: FormDefinitionForCreateNewRequest = {
            name: formDefinitionName != null ? formDefinitionName : this.formName,
            managerId: this.userService.currentUser.id ?? null,
            byRequestOnly: this.byRequestOnly,
            isEpisodeLocked: this.episodeLock,
            formCategoryId: this.selectedCategory,
            episodeId: this.episodeId,
            moduleId: this.moduleId,
            formRevisionData: {
                mobileFriendly: this.mobileFriendly,
                shareOnComplete: this.shareOnComplete,
                isEditable: this.isEditable,
                editableOnlyByInstantiator: this.isEditable && this.editableOnlyByInstantiator,
                json: JSON.stringify(this.form),
                mufCapable: this.mufCapable,
                pdfExportMode: this.selectedPdfExportMode,
                linkedDashboardId: this.selectedLinkedDashboardId,
            }
        };

        const customResponseHandlerSettings: IHttpResponseHandlerSettings = {
            showSuccessMessage: true,
            successMessage: "Form Design Created Successfully"
        };

        return await this.formIOService.sendFormJson(formJsonDefinition, customResponseHandlerSettings);
    };

    getInnerTabComponents(component: ExtendedComponentSchema) {
        let result: TabComponent[] = [];
        let children = component["components"] ?? component["columns"];
        const rows = component["rows"];
        if (!children && Array.isArray(rows)) {
            children = [];
            rows?.forEach(row => {
                children = children.concat(...row);
            });
        }
        if (children) {
            children.forEach(element => {
                // Call recursively to process inner components
                let components = this.getInnerComponents(element)
                const excludedComponents = [
                    "miproMedHistDatagrid",
                    "miproConsultHistDatagrid",
                    "miproEpisodeNotesDatagrid",
                ]
                components = components.filter(component => !excludedComponents.includes(component.type))

                result = result.concat(components);
            });
            const chosen = children.filter(v => v.type === "state-tabs");

            if (!chosen) {
                return [];
            }

            const tabs = chosen?.map(
                x =>
                    <TabComponent>{
                        tabControlAPIKey: x.key,
                        tabsKey: x.components.map(
                            c =>
                                <InnerTab>{
                                    key: c.key,
                                    label: c.label
                                }
                        )
                    }
            );
            return result.concat(tabs);
        } else {
            return result;
        }
    }

    private async updateRevision(increaseMinorVersion: boolean = false, saveAsMajorVersion: boolean = false) {
        const data: UpdateFormRevisionRequest = {
            json: JSON.stringify(this.form),
            mobileFriendly: this.mobileFriendly ?? false,
            shareOnComplete: this.shareOnComplete ?? false,
            isEditable: this.isEditable ?? false,
            editableOnlyByInstantiator: this.isEditable && this.editableOnlyByInstantiator,
            creatorId: this.userService.currentUser.id ?? null,
            note: this.revisionNote,
            mufCapable: this.mufCapable,
            increaseMinorVersion: increaseMinorVersion,
            saveAsMajorVersion: saveAsMajorVersion,
            formDefinitionName: this.formName,
            pdfExportMode: this.selectedPdfExportMode,
            linkedDashboardId: this.selectedLinkedDashboardId,
            episodeId: this.episodeId,
            isEpisodeLocked: this.episodeLock
        };

        const formJson = JSON.parse(JSON.stringify(this.form));
        const tabComponents = this.getInnerTabComponents(formJson).filter(x => x.tabControlAPIKey);

        if (tabComponents.length > 0) {
            data.tabs = tabComponents.flatMap(x =>
                x.tabsKey.map<TabObj>(i => {
                    return {
                        tabComponent: x.tabControlAPIKey,
                        innerTab: i.key
                    };
                })
            );
        }

        return await this.formIORevisionService.updateFormRevision(this.formRevisionId, data);
    };

    private async setAllDone(value: boolean) {
        this.status = value ? null : "*** Saving form failed. ***";

        if (value) this.clearJson();

        if (this.formObject && this.formObject.formCategoryId && this.formObject.formCategoryId != this.selectedCategory) {
            this.formIOService.update(
                this.formObject.id,
                {...this.formObject, formCategoryId: this.selectedCategory},
                {showSuccessMessage: false}
            ).then();
        }
        if (value) {
            const queryParams = {formId: this.formDefId};
            const queryParamsFromRoute = await firstValueFrom(this.activatedRoute.queryParams);
            const isFromHome = queryParamsFromRoute["isFromHome"] === "true";

            this.canLeaveWithoutWarning = true;

            if (!isFromHome) {
                setTimeout(() => {
                    this.router.navigate(["app/formio/list"], {queryParams});
                }, 250);
            } else {
                setTimeout(() => {
                    this.router.navigate(["app"]);
                }, 250);
            }
        }
    };

    // Scan the component list and check if there is any errors related to mixing component single and multiValue with the same api key and tag
    getErrorByMixingSingleAndMultipleValueComponentsWithSameApiKey_Tag(components: ExtendedComponentSchema[]) {
        let result = null;
        const groupedByApiKey = components.reduce((group, product) => {
            const key = product.key.replace(/\d+$/, "");
            group[key] = group[key] ?? [];
            group[key].push(product);
            return group;
        }, {});
        // Get all group keys
        const keyList = Object.getOwnPropertyNames(groupedByApiKey);
        // For each key analyze the group components for it's tags
        keyList.forEach(key => {
            const groupedByTag = groupedByApiKey[key].reduce((group, product) => {
                const key = product.tags?.length > 0 ? product.tags[0] : "";
                group[key] = group[key] ?? [];
                group[key].push(product);
                return group;
            }, {});
            const tagList = Object.getOwnPropertyNames(groupedByTag);
            // Check tag groups for any mixed component type (same api key and tag must have the same component type (single or multiple))
            tagList.forEach(tag => {
                const single = groupedByTag[tag].some(x => x.type != "select");
                const multiple = groupedByTag[tag].some(x => x.type == "select");
                if (single && multiple) {
                    result = {key: key, tag: tag};
                }
            });
        });
        return result;
    }

    // Recursive method to explore and expand each component and its inner components to check api keys
    getInnerComponents(component: ExtendedComponentSchema) {
        let children = component["components"] ?? component["columns"];
        const rows = component["rows"];
        if (!children && Array.isArray(rows)) {
            children = [];
            rows?.forEach(row => {
                children = children.concat(...row);
            });
        }
        let result = [];
        if (children) {
            children.forEach(element => {
                // Call recursively to process inner components
                result = result.concat(this.getInnerComponents(element));
            });
            result = result.concat(children.filter(v => (v.key as string)?.startsWith("dbo.")));
        }
        return result;
    }

    checkMUFCapableFormJSON(component: ExtendedComponentSchema) {
        const children = component["components"] ?? component["columns"];
        let result = 0;
        if (children) {
            children.forEach(element => {
                // Call recursively to process inner components
                result += this.checkMUFCapableFormJSON(element);
            });
            result += children.filter(v => v.type && (v.type as string).startsWith("state-tabs"))?.length ?? 0;
        }
        return result;
    }

    checkCustomGridMedHistDuplicateJSON(component: ExtendedComponentSchema) {
        const children = component["components"] ?? component["columns"];
        let result = 0;
        if (children) {
            children.forEach(element => {
                // Call recursively to process inner components
                result += this.checkCustomGridMedHistDuplicateJSON(element);
            });
            result += children.filter(v => v.type && (v.type as string) == "miproMedHistDatagrid")?.length ?? 0;
        }
        return result;
    }

    checkCustomGridEpisodeNotesDuplicateJSON(component: ExtendedComponentSchema) {
        const children = component["components"] ?? component["columns"];
        let result = 0;
        if (children) {
            children.forEach(element => {
                // Call recursively to process inner components
                result += this.checkCustomGridEpisodeNotesDuplicateJSON(element);
            });
            result += children.filter(v => v.type && (v.type as string) == "miproEpisodeNotesDatagrid")?.length ?? 0;
        }
        return result;
    }

    checkCustomGridConsultHistDuplicateJSON(component: ExtendedComponentSchema) {
        const children = component["components"] ?? component["columns"];
        let result = 0;
        if (children) {
            children.forEach(element => {
                // Call recursively to process inner components
                result += this.checkCustomGridConsultHistDuplicateJSON(element);
            });
            result += children.filter(v => v.type && (v.type as string) == "miproConsultHistDatagrid")?.length ?? 0;
        }
        return result;
    }

    checkMissingPropertiesOnReviewerTabs(component: ExtendedComponentSchema) {
        const children = component["components"] ?? component["columns"];
        let result = [];
        if (children) {
            children.forEach(element => {
                // Call recursively to process inner components
                const innerItems = this.checkMissingPropertiesOnReviewerTabs(element);
                if (innerItems.length > 0) result = [...result, innerItems];
            });
            const sameTab = children
                .filter(v => v.type && (v.type as string).startsWith("reviewerInput") && v.targetTab.key == component.key)
                .map(x => x.key);
            if (sameTab.length > 0) this.revInputTargetingOwnTab = [...this.revInputTargetingOwnTab, sameTab];
            const items = children
                .filter(v => v.type && (v.type as string).startsWith("reviewerInput") && !v.targetTab.key)
                .map(x => x.key);
            if (items.length > 0) result = [...result, items];
        }
        return result;
    }
}
